import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IGrowerAnalysisResponse, ISalesRepAnalysisResponse, ISellerAnalysisResponse } from './InsightsModels';
import { getAiExperience, getGrowerAnalysis, getSalesRepAnalyses, getSalesRepAnalysis, getSellerAnalysis, sendFeedbackForConcern } from './InsightThunks';

/** Insights is also known as Root Cause */

export type AiExperience = 'Unknown' | 'None' | 'MultiSalesRep' | 'SalesRep' | 'Seller' | 'Grower';

export interface IInsightsState
{
	isError: boolean;
	errorMessage: string;
	currentSalesRepId: string;
	currentSellerId: string;
	currentGrowerId: string;

	isLoadingSalesRepAnalyses: boolean;
	multiSalesRepAnalyses: ISalesRepAnalysisResponse[];
	singleSalesRepAnalysis: ISalesRepAnalysisResponse | null;
	singleSellerAnalysis: ISellerAnalysisResponse | null;
	singleGrowerAnalysis: IGrowerAnalysisResponse | null;
	
	isLoadingExperience: boolean;
	aiExperience: AiExperience | null;

	isLoadingFeedback: boolean;
}

export const initialState: IInsightsState = {
	isError: false,	
	errorMessage: undefined,
	currentSalesRepId: undefined,
	currentSellerId: undefined,
	currentGrowerId: undefined,

	isLoadingSalesRepAnalyses: false,
	multiSalesRepAnalyses: [],
	singleSalesRepAnalysis: null,
	singleSellerAnalysis: null,
	singleGrowerAnalysis: null,

	aiExperience: null,
	isLoadingExperience: false,

	isLoadingFeedback: false,
};

export const insightsSlice = createSlice({
	name: 'insights',
	initialState,
	reducers: {
		clearState: (state) => 
		{
			state.isError = false;
			state.errorMessage = undefined;
			state.isLoadingSalesRepAnalyses = false;
			state.isLoadingExperience = false;

			state.currentGrowerId = undefined;
			state.currentSellerId = undefined;
			state.currentSalesRepId = undefined;

			state.multiSalesRepAnalyses = [];
			state.singleSalesRepAnalysis = null;
			state.singleSellerAnalysis = null;
			state.singleGrowerAnalysis = null;

			state.aiExperience = null;

			return state;
		},
		clearError: (state) =>
		{
			state.isError = false;
			state.errorMessage = undefined;

			return state;
		},
		clearSelectedSalesRepAnalysis: (state) =>
		{
			state.currentSalesRepId = undefined;
			state.singleSalesRepAnalysis = null;

			return state;
		},
		clearSelectedSellerAnalysis: (state) =>
		{
			state.currentSellerId = undefined;
			state.singleSellerAnalysis = null;

			return state;
		},
		clearSelectedGrowerAnalysis: (state) =>
		{
			state.currentGrowerId = undefined;
			state.singleGrowerAnalysis = null;

			return state;
		},
		setSelectedSalesRepId: (state, { payload } : PayloadAction<string>) =>
		{
			state.currentSalesRepId = payload;
			return state;
		},
		setSelectedSellerId: (state, { payload } : PayloadAction<string>) =>
		{
			state.currentSellerId = payload;
			return state;
		},
		setSelectedGrowerId: (state, { payload } : PayloadAction<string>) =>
		{
			state.currentGrowerId = payload;
			return state;
		},
	},
	extraReducers: (builder) =>
	{
		// AI Experience
		builder.addCase(getAiExperience.pending, (state, action) =>
		{
			state.isLoadingExperience = true;		
		});
		builder.addCase(getAiExperience.fulfilled, (state, action) =>
		{
			state.isLoadingExperience = false;
			state.aiExperience = action.payload as AiExperience;
		});
		builder.addCase(getAiExperience.rejected, (state, action) =>
		{
			state.isLoadingExperience = false;
			state.isError = true;
			state.aiExperience = 'Unknown';

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the AI User Experience type. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the AI User Experience type. Please refresh and try again.';
			}

		});

		// Multi Sales Rep Analyses
		builder.addCase(getSalesRepAnalyses.pending, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = true;
		});
		builder.addCase(getSalesRepAnalyses.fulfilled, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = false;
			state.multiSalesRepAnalyses = action.payload as ISalesRepAnalysisResponse[];
		});
		builder.addCase(getSalesRepAnalyses.rejected, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Sales Rep Analyses. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Sales Rep Analyses. Please refresh and try again.';
			}

		});


		// Single Sales Rep Analyses
		builder.addCase(getSalesRepAnalysis.pending, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = true;
		});
		builder.addCase(getSalesRepAnalysis.fulfilled, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = false;
			state.singleSalesRepAnalysis = action.payload as ISalesRepAnalysisResponse;
		});
		builder.addCase(getSalesRepAnalysis.rejected, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Sales Rep Analysis. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Sales Rep Analysis. Please refresh and try again.';
			}

		});

		// Single Seller Analyses
		builder.addCase(getSellerAnalysis.pending, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = true;
		});
		builder.addCase(getSellerAnalysis.fulfilled, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = false;
			state.singleSellerAnalysis = action.payload as ISellerAnalysisResponse;
		});
		builder.addCase(getSellerAnalysis.rejected, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Seller Analysis. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Seller Analysis. Please refresh and try again.';
			}

		});

		// Single Grower Analyses
		builder.addCase(getGrowerAnalysis.pending, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = true;
		});
		builder.addCase(getGrowerAnalysis.fulfilled, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = false;
			state.singleGrowerAnalysis = action.payload as IGrowerAnalysisResponse;
		});
		builder.addCase(getGrowerAnalysis.rejected, (state, action) =>
		{
			state.isLoadingSalesRepAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Grower Analysis. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Grower Analysis. Please refresh and try again.';
			}

		});

		// Concern Feedback Post
		builder.addCase(sendFeedbackForConcern.pending, (state, action) =>
		{
			state.isLoadingFeedback = true;
		});
		builder.addCase(sendFeedbackForConcern.fulfilled, (state, action) =>
		{
			state.isLoadingFeedback = false;
		});
		builder.addCase(sendFeedbackForConcern.rejected, (state, action) =>
		{
			state.isLoadingFeedback = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to send the feedback. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem sending the feedback. Please refresh and try again.';
			}

		});
	},
});

export const {
	clearState,
	clearError,
	clearSelectedGrowerAnalysis,
	clearSelectedSellerAnalysis,
	clearSelectedSalesRepAnalysis,
	setSelectedSalesRepId,
	setSelectedSellerId,
	setSelectedGrowerId
} = insightsSlice.actions;